import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Plus,
  Trash2,
  GripVertical,
  Info,
  ChevronDown,
  ChevronUp,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// Types
export interface WorkflowLevel {
  id: string;
  levelNumber: number;
  followUpDays: number;
  escalationDays: number;
  assignedUser: string;
  documentTemplate: string;
}

export interface WorkflowData {
  workflowName: string;
  disputeCategory: string;
  description: string;
  levels: WorkflowLevel[];
}

export interface DisputeCategory {
  id: string;
  name: string;
  subtitle: string;
}

// Mock Data
const mockUsers = [
  'Sarah Johnson',
  'John Smith',
  'Michael Chen',
  'Emma Williams',
  'David Martinez',
];

const mockDocumentTemplates = [
  'Initial Dispute Acknowledgment',
  'Dispute Investigation Letter',
  'Payment Plan Proposal',
  'Final Resolution Notice',
  'Escalation to Management',
];

const mockDisputeCategories: DisputeCategory[] = [
  {
    id: 'pricing',
    name: 'Pricing Dispute',
    subtitle: 'Disputes related to pricing, discounts, or contract terms',
  },
  {
    id: 'delivery',
    name: 'Delivery Dispute',
    subtitle: 'Issues with delivery timing or damaged goods',
  },
  {
    id: 'quality',
    name: 'Quality Dispute',
    subtitle: 'Product or service quality concerns',
  },
  {
    id: 'contract',
    name: 'Contract Terms Dispute',
    subtitle: 'Disagreements about contract interpretation',
  },
];

interface WorkflowEditorProps {
  open: boolean;
  onClose: () => void;
  onSave: (workflow: WorkflowData) => void;
  initialData?: WorkflowData | null;
}

export function WorkflowEditor({ open, onClose, onSave, initialData }: WorkflowEditorProps) {
  const [currentTab, setCurrentTab] = useState('basic');
  const [workflowName, setWorkflowName] = useState('');
  const [disputeCategory, setDisputeCategory] = useState('');
  const [description, setDescription] = useState('');
  const [levels, setLevels] = useState<WorkflowLevel[]>([]);
  const [isTimelineExpanded, setIsTimelineExpanded] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  // Load initial data or set defaults
  useEffect(() => {
    if (open) {
      if (initialData) {
        setWorkflowName(initialData.workflowName);
        setDisputeCategory(initialData.disputeCategory);
        setDescription(initialData.description);
        setLevels(initialData.levels);
      } else {
        // Set default data for demo
        setWorkflowName('Standard Pricing Dispute Workflow');
        setDisputeCategory('pricing');
        setDescription('Standard workflow for handling pricing disputes');
        setLevels([
          {
            id: 'level-1',
            levelNumber: 1,
            followUpDays: 3,
            escalationDays: 7,
            assignedUser: 'Sarah Johnson',
            documentTemplate: 'Initial Dispute Acknowledgment',
          },
          {
            id: 'level-2',
            levelNumber: 2,
            followUpDays: 5,
            escalationDays: 10,
            assignedUser: 'John Smith',
            documentTemplate: 'Dispute Investigation Letter',
          },
        ]);
      }
      setCurrentTab('basic');
      setErrors({});
    }
  }, [open, initialData]);

  // Calculate timeline data
  const timelineData = useMemo(() => {
    let cumulativeFollowUp = 0;
    let cumulativeEscalation = 0;

    const levelTimelines = levels.map((level, index) => {
      if (index === 0) {
        cumulativeFollowUp = level.followUpDays;
        cumulativeEscalation = level.escalationDays;
      } else {
        const prevEscalation = cumulativeEscalation;
        cumulativeFollowUp = prevEscalation + level.followUpDays;
        cumulativeEscalation = prevEscalation + level.escalationDays;
      }

      return {
        levelNumber: level.levelNumber,
        followUpDay: cumulativeFollowUp,
        escalationDay: cumulativeEscalation,
        assignedUser: level.assignedUser,
        documentTemplate: level.documentTemplate,
      };
    });

    const totalDuration = levelTimelines.length > 0 
      ? levelTimelines[levelTimelines.length - 1].escalationDay 
      : 0;

    return { levelTimelines, totalDuration };
  }, [levels]);

  // Validation
  const validateBasicInfo = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!workflowName.trim()) {
      newErrors.workflowName = 'Workflow name is required';
    }

    if (!disputeCategory) {
      newErrors.disputeCategory = 'Dispute category is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateWorkflowLevels = (): boolean => {
    const newErrors: Record<string, string> = {};

    levels.forEach((level, index) => {
      if (level.followUpDays <= 0) {
        newErrors[`level-${index}-followUp`] = 'Follow-up days must be greater than 0';
      }

      if (level.escalationDays <= 0) {
        newErrors[`level-${index}-escalation`] = 'Escalation days must be greater than 0';
      }

      if (level.escalationDays <= level.followUpDays) {
        newErrors[`level-${index}-escalation`] = 'Escalation days must be greater than follow-up days';
      }

      if (!level.assignedUser) {
        newErrors[`level-${index}-user`] = 'Assigned user is required';
      }

      if (!level.documentTemplate) {
        newErrors[`level-${index}-template`] = 'Document template is required';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid = (): boolean => {
    return (
      workflowName.trim() !== '' &&
      disputeCategory !== '' &&
      levels.length > 0 &&
      levels.every(
        (level) =>
          level.followUpDays > 0 &&
          level.escalationDays > level.followUpDays &&
          level.assignedUser &&
          level.documentTemplate
      )
    );
  };

  // Level management
  const addLevel = () => {
    if (levels.length >= 5) {
      toast.error('Maximum 5 levels reached');
      return;
    }

    const newLevel: WorkflowLevel = {
      id: `level-${Date.now()}`,
      levelNumber: levels.length + 1,
      followUpDays: 0,
      escalationDays: 0,
      assignedUser: '',
      documentTemplate: '',
    };

    setLevels([...levels, newLevel]);
  };

  const removeLevel = (index: number) => {
    const updatedLevels = levels.filter((_, i) => i !== index);
    // Renumber levels
    const renumberedLevels = updatedLevels.map((level, i) => ({
      ...level,
      levelNumber: i + 1,
    }));
    setLevels(renumberedLevels);
  };

  const updateLevel = (index: number, field: keyof WorkflowLevel, value: any) => {
    const updatedLevels = [...levels];
    updatedLevels[index] = { ...updatedLevels[index], [field]: value };
    setLevels(updatedLevels);
  };

  const moveLevel = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= levels.length) return;

    const updatedLevels = [...levels];
    const [movedLevel] = updatedLevels.splice(fromIndex, 1);
    updatedLevels.splice(toIndex, 0, movedLevel);

    // Renumber levels
    const renumberedLevels = updatedLevels.map((level, i) => ({
      ...level,
      levelNumber: i + 1,
    }));

    setLevels(renumberedLevels);
  };

  // Drag and drop handlers
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    moveLevel(draggedIndex, index);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Save handler
  const handleSave = () => {
    if (currentTab === 'basic') {
      if (!validateBasicInfo()) {
        return;
      }
      setCurrentTab('workflow');
      return;
    }

    if (!validateWorkflowLevels()) {
      return;
    }

    const workflowData: WorkflowData = {
      workflowName,
      disputeCategory,
      description,
      levels,
    };

    onSave(workflowData);
    toast.success('Workflow updated successfully');
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const selectedCategory = mockDisputeCategories.find((cat) => cat.id === disputeCategory);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[800px] max-h-[90vh] p-0">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle>Edit Workflow</DialogTitle>
        </DialogHeader>

        <Tabs value={currentTab} onValueChange={setCurrentTab} className="flex-1">
          <div className="px-6">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="basic">Basic Information</TabsTrigger>
              <TabsTrigger value="workflow">
                Workflow Levels
                {levels.length > 0 && (
                  <Badge variant="secondary" className="ml-2 bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400">
                    {levels.length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>
          </div>

          <ScrollArea className="flex-1 px-6" style={{ maxHeight: 'calc(90vh - 200px)' }}>
            {/* Basic Information Tab */}
            <TabsContent value="basic" className="mt-4 space-y-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="workflowName">
                    Workflow Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="workflowName"
                    value={workflowName}
                    onChange={(e) => setWorkflowName(e.target.value)}
                    placeholder="Enter workflow name"
                    className={errors.workflowName ? 'border-red-500' : ''}
                  />
                  {errors.workflowName && (
                    <p className="text-sm text-red-500">{errors.workflowName}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="disputeCategory">
                    Dispute Category <span className="text-red-500">*</span>
                  </Label>
                  <Select value={disputeCategory} onValueChange={setDisputeCategory}>
                    <SelectTrigger className={errors.disputeCategory ? 'border-red-500' : ''}>
                      <SelectValue placeholder="Select dispute category" />
                    </SelectTrigger>
                    <SelectContent>
                      {mockDisputeCategories.map((category) => (
                        <SelectItem key={category.id} value={category.id}>
                          <div className="flex flex-col">
                            <span className="font-medium">{category.name}</span>
                            <span className="text-xs text-muted-foreground">
                              {category.subtitle}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedCategory && (
                    <p className="text-sm text-muted-foreground">{selectedCategory.subtitle}</p>
                  )}
                  {errors.disputeCategory && (
                    <p className="text-sm text-red-500">{errors.disputeCategory}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Enter workflow description"
                    rows={4}
                  />
                  <p className="text-sm text-muted-foreground">
                    Provide a clear description to help team members understand when to use this workflow.
                  </p>
                </div>

                <Card className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900">
                  <CardContent className="pt-4">
                    <div className="flex gap-3">
                      <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                          Next Step
                        </p>
                        <p className="text-sm text-blue-800 dark:text-blue-200">
                          After completing the basic information, navigate to the 'Workflow Levels' tab to configure escalation levels, timelines, and assignments.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Workflow Levels Tab */}
            <TabsContent value="workflow" className="mt-4 space-y-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold">Workflow Levels</h3>
                    <p className="text-sm text-muted-foreground">
                      Define escalation levels with timelines and assignments
                    </p>
                  </div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          onClick={addLevel}
                          disabled={levels.length >= 5}
                          size="sm"
                          className="bg-teal-600 hover:bg-teal-700 text-white"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Level
                        </Button>
                      </TooltipTrigger>
                      {levels.length >= 5 && (
                        <TooltipContent>
                          <p>Maximum 5 levels reached</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                </div>

                {/* Workflow Level Cards */}
                <div className="space-y-3">
                  {levels.length === 0 ? (
                    <Card className="border-dashed">
                      <CardContent className="flex flex-col items-center justify-center py-8">
                        <p className="text-muted-foreground mb-4">No workflow levels defined</p>
                        <Button onClick={addLevel} variant="outline" size="sm">
                          <Plus className="h-4 w-4 mr-2" />
                          Add First Level
                        </Button>
                      </CardContent>
                    </Card>
                  ) : (
                    levels.map((level, index) => (
                      <Card
                        key={level.id}
                        className="border hover:border-teal-300 dark:hover:border-teal-700 transition-colors"
                        draggable
                        onDragStart={() => handleDragStart(index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragEnd={handleDragEnd}
                      >
                        <CardContent className="py-4">
                          <div className="flex items-start gap-3">
                            {/* Drag Handle and Level Badge */}
                            <div className="flex items-center gap-2 flex-shrink-0 pt-2">
                              <div className="cursor-grab active:cursor-grabbing">
                                <GripVertical className="h-5 w-5 text-muted-foreground" />
                              </div>
                              <Badge className="bg-teal-600 text-white whitespace-nowrap">
                                Level {level.levelNumber}
                              </Badge>
                            </div>

                            {/* All Fields in a Single Row */}
                            <div className="flex-1 grid grid-cols-4 gap-3">
                              {/* Follow Up Days */}
                              <div className="space-y-1.5">
                                <Label htmlFor={`followUpDays-${index}`} className="text-xs">
                                  Follow Up Days <span className="text-red-500">*</span>
                                </Label>
                                <Input
                                  id={`followUpDays-${index}`}
                                  type="number"
                                  min="1"
                                  value={level.followUpDays || ''}
                                  onChange={(e) =>
                                    updateLevel(index, 'followUpDays', parseInt(e.target.value) || 0)
                                  }
                                  placeholder="Days"
                                  className={
                                    errors[`level-${index}-followUp`] ? 'border-red-500 h-9' : 'h-9'
                                  }
                                />
                                {errors[`level-${index}-followUp`] && (
                                  <p className="text-xs text-red-500">
                                    {errors[`level-${index}-followUp`]}
                                  </p>
                                )}
                              </div>

                              {/* Escalation Days */}
                              <div className="space-y-1.5">
                                <Label htmlFor={`escalationDays-${index}`} className="text-xs">
                                  Escalation Days <span className="text-red-500">*</span>
                                </Label>
                                <Input
                                  id={`escalationDays-${index}`}
                                  type="number"
                                  min="1"
                                  value={level.escalationDays || ''}
                                  onChange={(e) =>
                                    updateLevel(index, 'escalationDays', parseInt(e.target.value) || 0)
                                  }
                                  placeholder="Days"
                                  className={
                                    errors[`level-${index}-escalation`] ? 'border-red-500 h-9' : 'h-9'
                                  }
                                />
                                {errors[`level-${index}-escalation`] && (
                                  <p className="text-xs text-red-500">
                                    {errors[`level-${index}-escalation`]}
                                  </p>
                                )}
                              </div>

                              {/* Assigned User */}
                              <div className="space-y-1.5">
                                <Label htmlFor={`assignedUser-${index}`} className="text-xs">
                                  Assigned User <span className="text-red-500">*</span>
                                </Label>
                                <Select
                                  value={level.assignedUser}
                                  onValueChange={(value) => updateLevel(index, 'assignedUser', value)}
                                >
                                  <SelectTrigger
                                    className={errors[`level-${index}-user`] ? 'border-red-500 h-9' : 'h-9'}
                                  >
                                    <SelectValue placeholder="Select user" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {mockUsers.map((user) => (
                                      <SelectItem key={user} value={user}>
                                        {user}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                {errors[`level-${index}-user`] && (
                                  <p className="text-xs text-red-500">{errors[`level-${index}-user`]}</p>
                                )}
                              </div>

                              {/* Document Template */}
                              <div className="space-y-1.5">
                                <Label htmlFor={`documentTemplate-${index}`} className="text-xs">
                                  Document Template <span className="text-red-500">*</span>
                                </Label>
                                <Select
                                  value={level.documentTemplate}
                                  onValueChange={(value) =>
                                    updateLevel(index, 'documentTemplate', value)
                                  }
                                >
                                  <SelectTrigger
                                    className={
                                      errors[`level-${index}-template`] ? 'border-red-500 h-9' : 'h-9'
                                    }
                                  >
                                    <SelectValue placeholder="Select template" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {mockDocumentTemplates.map((template) => (
                                      <SelectItem key={template} value={template}>
                                        {template}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                {errors[`level-${index}-template`] && (
                                  <p className="text-xs text-red-500">
                                    {errors[`level-${index}-template`]}
                                  </p>
                                )}
                              </div>
                            </div>

                            {/* Delete Button */}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeLevel(index)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20 flex-shrink-0 h-9 w-9 p-0 mt-6"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>

                {/* Workflow Timeline Section */}
                {levels.length > 0 && (
                  <Collapsible open={isTimelineExpanded} onOpenChange={setIsTimelineExpanded}>
                    <Card className="bg-gray-50 dark:bg-gray-900/50">
                      <CardHeader className="pb-3">
                        <CollapsibleTrigger className="flex items-center justify-between w-full hover:opacity-80">
                          <CardTitle className="text-base">Workflow Timeline</CardTitle>
                          {isTimelineExpanded ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </CollapsibleTrigger>
                      </CardHeader>
                      <CollapsibleContent>
                        <CardContent className="space-y-3">
                          {timelineData.levelTimelines.map((timeline, index) => (
                            <div
                              key={index}
                              className="p-3 bg-white dark:bg-gray-800 rounded-lg border"
                            >
                              <div className="flex items-center gap-2 mb-2">
                                <Badge className="bg-teal-600 text-white">
                                  Level {timeline.levelNumber}
                                </Badge>
                                <span className="text-sm font-medium">
                                  Follow-up: Day {timeline.followUpDay} • Escalates: Day{' '}
                                  {timeline.escalationDay}
                                </span>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                Assigned to: {timeline.assignedUser} • Template:{' '}
                                {timeline.documentTemplate}
                              </p>
                            </div>
                          ))}

                          <div className="pt-2 border-t">
                            <p className="text-sm font-semibold">
                              Total Duration:{' '}
                              <span className="text-teal-600 dark:text-teal-400">
                                Up to {timelineData.totalDuration} days
                              </span>
                            </p>
                          </div>
                        </CardContent>
                      </CollapsibleContent>
                    </Card>
                  </Collapsible>
                )}
              </div>
            </TabsContent>
          </ScrollArea>
        </Tabs>

        <DialogFooter className="px-6 py-4 border-t">
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!isFormValid()}
            className="bg-teal-600 hover:bg-teal-700 text-white"
          >
            {currentTab === 'basic' ? 'Next: Workflow Levels' : 'Update Workflow'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
